// Laster siden automatisk på nytt når fanen kjører en eldre bundle enn den
// serveren tilbyr. Lagertelefonene står åpne i dagevis, så en reell retur
// etter natt eller lang pause skal hente siste versjon. Bundle-filnavnet
// (index-<hash>.js) sammenlignes med /api/version, og samme serverversjon
// lastes aldri automatisk mer enn én gang per fane.
//
// Automatisk reload er med vilje konservativ: fanen må ha vært
// sammenhengende skjult i minst 30 minutter. Focus uten en forutgående
// visibilitychange til hidden, som når kamera eller filvelger lukkes, kan
// aldri utløse reload. Det finnes heller ingen intervallsjekk midt i bruk.
//
// Nginx-loggene 22/7 viste en reload samtidig med POST /media (499). Derfor
// blokkerer en aktiv opplasting alltid reload (se upload-guard.ts). Når
// opplastingen blir ferdig, skjer ingenting automatisk. En blokkert reload
// kan først prøves igjen etter en ny, reell skjuling på minst 30 minutter.
// UpdateBanner i version-banner.ts gir fortsatt manuell reload hele dagen.

import { isUploadBusy } from "./upload-guard";

const RELOADED_KEY = "stale-bundle-reloaded-for";
const CHUNK_RELOAD_KEY = "chunk-load-reload-attempted";
const MIN_CHECK_INTERVAL_MS = 60_000;
export const MIN_HIDDEN_MS = 30 * 60_000;

const CHUNK_ERROR_PATTERNS = [
  "chunkloaderror",
  "loading chunk",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "unable to preload css",
];

let chunkReloadInProgress = false;

export function isChunkLoadError(error: unknown): boolean {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  const description = `${name} ${message}`.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => description.includes(pattern));
}

export function shouldReloadForChunkError(
  error: unknown,
  alreadyReloaded: boolean,
  uploadBusy: boolean,
): boolean {
  return isChunkLoadError(error) && !alreadyReloaded && !uploadBusy;
}

export function parseBundleVersion(src: string): string | null {
  const match = src.match(/\/assets\/(index-[\w-]+\.js)/);
  return match ? match[1] : null;
}

export interface ShouldReloadContext {
  uploadBusy: boolean;
  hiddenMs: number | null;
}

export function shouldReload(
  own: string | null,
  server: string | null,
  alreadyReloadedFor: string | null,
  ctx: ShouldReloadContext = { uploadBusy: false, hiddenMs: null },
): boolean {
  // Aktivt arbeid og focus uten en reell, lang skjuling skal aldri avbrytes.
  if (ctx.uploadBusy) return false;
  if (ctx.hiddenMs === null || ctx.hiddenMs < MIN_HIDDEN_MS) return false;
  // Both sides must be known (dev mode has neither), differ, and we must not
  // already have reloaded for this server version (sessionStorage survives
  // the reload in the same tab, so a bad server value can't cause a loop).
  return !!own && !!server && own !== server && alreadyReloadedFor !== server;
}

export function ownBundleVersion(): string | null {
  const tags = document.querySelectorAll<HTMLScriptElement>("script[src]");
  for (const tag of Array.from(tags)) {
    const version = parseBundleVersion(tag.src);
    if (version) return version;
  }
  // Dev mode: vite serves /src/main.tsx, no hashed bundle -> check disabled.
  return null;
}

let lastCheckAt = 0;
// null betyr at ingen visibilitychange til hidden venter på å bli konsumert.
let lastHiddenAt: number | null = null;

function hiddenMsSinceReturn(): number | null {
  return lastHiddenAt === null ? null : Date.now() - lastHiddenAt;
}

function performReload(server: string): void {
  try {
    sessionStorage.setItem(RELOADED_KEY, server);
  } catch {
    /* see below */
  }
  window.location.reload();
}

async function checkOnce(hiddenMs: number | null): Promise<void> {
  const own = ownBundleVersion();
  if (!own) return;

  const now = Date.now();
  if (now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
  lastCheckAt = now;

  let server: string | null = null;
  try {
    const res = await fetch("/api/version", { cache: "no-store" });
    if (!res.ok) return;
    const body = await res.json();
    server = typeof body?.version === "string" ? body.version : null;
  } catch {
    return; // offline/transient: try again on next return-to-tab
  }

  let alreadyReloadedFor: string | null = null;
  try {
    alreadyReloadedFor = sessionStorage.getItem(RELOADED_KEY);
  } catch {
    // sessionStorage unavailable (private mode quirks): skip the guard write
    // path too, by treating every mismatch as not-yet-reloaded. The 60s
    // throttle still prevents rapid loops.
  }

  const uploadBusy = isUploadBusy();

  if (shouldReload(own, server, alreadyReloadedFor, { uploadBusy, hiddenMs })) {
    performReload(server as string);
  }
}

function reloadAfterChunkLoadError(error: unknown): boolean {
  if (chunkReloadInProgress) return true;

  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
  } catch {
    // Without sessionStorage we cannot guarantee a one-reload cap. Let the
    // existing ErrorBoundary show its retry UI instead of risking a loop.
    return false;
  }

  if (!shouldReloadForChunkError(error, alreadyReloaded, isUploadBusy())) {
    return false;
  }

  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch {
    return false;
  }

  chunkReloadInProgress = true;
  console.warn("[stale-bundle] Route chunk unavailable; reloading once for fresh assets");
  window.location.reload();
  return true;
}

function clearChunkReloadGuard(): void {
  if (chunkReloadInProgress) return;
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // A successful import needs no recovery even if storage is unavailable.
  }
}

export async function loadRouteChunk<T>(loader: () => Promise<T>): Promise<T> {
  try {
    const routeModule = await loader();
    // En stale/mikset chunk-graf etter deploy kan gi en «vellykket» import der
    // default-eksporten React.lazy trenger mangler. Da krasjet renderen med
    // «Cannot read properties of undefined (reading 'default')» (UI-0003
    // 12/8) uten at noe mønster i CHUNK_ERROR_PATTERNS matchet. Klassifiser
    // det som chunk-feil her, før React rekker å rendre.
    if (routeModule == null || (routeModule as { default?: unknown }).default == null) {
      throw new Error("Loading chunk failed: route module missing default export");
    }
    clearChunkReloadGuard();
    return routeModule;
  } catch (error) {
    if (reloadAfterChunkLoadError(error)) {
      // Keep Suspense mounted while the browser navigates. A second failure
      // after reload is thrown below and handled by the app ErrorBoundary.
      return new Promise<T>(() => undefined);
    }
    throw error;
  }
}

export function initStaleBundleReload(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const preloadError = (event as Event & { payload?: unknown }).payload;
    if (reloadAfterChunkLoadError(preloadError)) {
      event.preventDefault();
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      const hiddenMs = hiddenMsSinceReturn();
      lastHiddenAt = null;
      void checkOnce(hiddenMs);
    } else if (document.visibilityState === "hidden") {
      lastHiddenAt = Date.now();
    }
  });
  window.addEventListener("focus", () => {
    if (document.visibilityState !== "visible" || lastHiddenAt === null) return;
    const hiddenMs = hiddenMsSinceReturn();
    lastHiddenAt = null;
    void checkOnce(hiddenMs);
  });
}
